import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect } from 'react';
import { useRemountFileInput } from '../../hooks/useRemountFileInput';
import { Transition } from '../../components/Transition';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { useAppDispatch, useAppSelector } from '../../store';
import { findSelectBundleCategory } from '../../store/selectBundleCategory/api';
import { DocumentType } from '../../enum';
import { uploadAssets } from '../../utils/uploadAssets';
import { getAssetURl } from '../../utils/url';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const BundleSubcategoryFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const dispatch = useAppDispatch();
  const { data: categories, status } = useAppSelector(
    (state) => state.selectBundleCategory
  );
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const isEdit = Boolean(values?.id);
  const { fileInputRef, fileInputKey, clearFileInput } = useRemountFileInput(
    toggle,
    values?.id,
    Boolean(values?.imgUrl),
    'imgUrl'
  );

  useEffect(() => {
    if (toggle && status === 'idle') {
      dispatch(findSelectBundleCategory({}));
    }
  }, [toggle, status, dispatch]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        clearFileInput();
        resetForm();
        onClose();
      }}
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
        {isEdit ? 'Edit Bundle Subcategory' : 'New Bundle Subcategory'}
      </DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
            >
              <FormControl
                fullWidth
                error={Boolean(errors.bundleCategoryId && touched.bundleCategoryId)}
              >
                <InputLabel id='bundle-subcategory-category-label'>
                  Bundle Category
                </InputLabel>
                <Select
                  labelId='bundle-subcategory-category-label'
                  id='bundleCategoryId'
                  name='bundleCategoryId'
                  label='Bundle Category'
                  value={values.bundleCategoryId || ''}
                  onChange={(e) =>
                    setFieldValue('bundleCategoryId', e.target.value)
                  }
                >
                  {categories?.map((category) => (
                    <MenuItem
                      key={category.id}
                      value={category.id}
                    >
                      {category.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='labelEn'
                name='labelEn'
                label='Label English'
                placeholder='Subcategory name'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelEn}
                error={Boolean(errors.labelEn && touched.labelEn)}
                helperText={
                  Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='labelAr'
                name='labelAr'
                label='Label Arabic'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelAr}
                error={Boolean(errors.labelAr && touched.labelAr)}
                helperText={
                  Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                }
              />
            </Grid>

            <Grid
              item
              xs={values?.imgUrl ? 8.5 : 12}
              sm={values?.imgUrl ? 8.5 : 12}
            >
              <TextField
                key={fileInputKey}
                type='file'
                fullWidth
                name='imgUrl'
                id='imgUrl'
                inputRef={fileInputRef}
                onChange={(e) =>
                  handleFileChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    DocumentType.category,
                    'imgUrl'
                  )
                }
                inputProps={{ multiple: false, accept: 'image/*' }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                label={'Image URL'}
                error={Boolean(errors.imgUrl && touched.imgUrl)}
                helperText={
                  Boolean(errors.imgUrl && touched.imgUrl) && errors.imgUrl
                }
              />
            </Grid>
            {values?.imgUrl && (
              <Grid
                item
                xs={3.5}
                sm={3.5}
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    imgProps={{ crossOrigin: 'anonymous' }}
                    src={getAssetURl(values?.imgUrl, assetUrl)}
                    sx={{
                      borderRadius: 'unset',
                      m: 1,
                      objectFit: 'contain',
                      '& img': { objectFit: 'contain' },
                      width: { xs: '75px', sm: '100px' },
                      height: 50,
                    }}
                  />
                  <IconButton
                    size='small'
                    aria-label='Remove image'
                    onClick={() => {
                      setFieldValue('imgUrl', '');
                      clearFileInput();
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      width: 22,
                      height: 22,
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='position'
                name='position'
                label='Position'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.position}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                error={Boolean(errors.position && touched.position)}
                helperText={
                  Boolean(errors.position && touched.position) &&
                  errors.position
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <FormControl fullWidth>
                <InputLabel id='bundle-subcategory-status-label'>Status</InputLabel>
                <Select
                  labelId='bundle-subcategory-status-label'
                  id='activeState'
                  name='activeState'
                  label='Status'
                  value={values.activeState ? 'active' : 'inactive'}
                  onChange={(e) =>
                    setFieldValue('activeState', e.target.value === 'active')
                  }
                >
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='inactive'>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              clearFileInput();
              resetForm();
              onClose();
            }}
            variant='outlined'
          >
            Cancel
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {isEdit ? 'Update Subcategory' : 'Create Subcategory'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BundleSubcategoryFormModal;
